import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Scale, ShieldCheck, Check, Loader2, LogOut, CreditCard, AlertTriangle, Gift } from "lucide-react";
import { EnterpriseCrmSection, EnterpriseValuePanel } from "@/components/EnterpriseCrm";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// "Membership Required" paywall shown to attorneys without an active subscription.
export default function MembershipPaywall({ membership, onRefresh }) {
  const { user, logout } = useAuth();
  const [accepted, setAccepted] = useState(!!membership?.terms_accepted);
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState("");
  const [applying, setApplying] = useState(false);
  const m = membership || {};
  const level = m.membership_level || m.default_plan?.name || "Attorney Membership";
  const monthly = m.monthly_price ?? m.default_plan?.monthly_price ?? 499;
  const claim = m.claim_fee ?? m.default_plan?.claim_fee ?? 100;
  const drone = m.drone_fee ?? m.default_plan?.drone_fee ?? 250;

  const pay = async () => {
    if (!accepted) { toast.error("Please accept the membership terms."); return; }
    setBusy(true);
    try {
      await api.post("/attorney/membership/accept-terms", { accepted: true });
      const { data } = await api.post("/billing/checkout", { kind: "membership", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/attorney/membership/apply-promo", { code: promo.trim() });
      toast.success(`${data.code} applied — ${data.description}`);
      setPromo("");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Invalid promo code");
    } finally { setApplying(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="membership-paywall">
      <div className="w-full max-w-lg bg-white rounded-3xl border border-slate-100 shadow-[0_20px_60px_rgb(10,25,47,0.12)] overflow-hidden">
        <div className="bg-navy text-white p-7 text-center relative">
          <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 50% 0%, rgba(56,189,248,0.4), transparent 60%)" }} />
          <div className="relative">
            <div className="inline-flex h-14 w-14 rounded-2xl bg-white/10 items-center justify-center mb-3"><Scale className="w-7 h-7 text-cyan-300" /></div>
            <h1 className="font-heading text-3xl font-extrabold">Membership Required</h1>
            <p className="mt-2 text-slate-300 text-sm max-w-sm mx-auto">Activate your Crash Ready attorney membership to access your portal, claim cases, and use firm tools.</p>
          </div>
        </div>

        <div className="p-7">
          {m.payment_failed && (
            <div className="mb-4 rounded-2xl bg-red-50 border border-red-200 p-3 flex items-start gap-2 text-sm text-red-700" data-testid="membership-payment-failed">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Your last payment failed. Please update your payment method to restore access.
            </div>
          )}
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-cobalt">{level}</span>
              <ShieldCheck className="w-4 h-4 text-cobalt" />
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-heading text-4xl font-extrabold text-navy" data-testid="paywall-monthly-price">{money(monthly)}</span>
              <span className="text-slate-500 text-sm">/month</span>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              {[["Full Attorney Portal & Firm Command Center", true], ["Marketplace claim fee", `${money(claim)} per case`], ["Drone deployment fee", `${money(drone)} per deployment`], ["Recurring monthly — cancel anytime", true]].map(([label, val], i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-slate-600"><Check className="w-4 h-4 text-emerald-500" /> {label}</span>
                  {typeof val === "string" && <span className="text-slate-500 font-medium">{val}</span>}
                </div>
              ))}
            </div>
            {m.promo_label && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="paywall-promo-label">
                <Gift className="w-3.5 h-3.5" /> {m.promo_label}
              </div>
            )}
          </div>

          <div className="mt-4 space-y-3"><EnterpriseCrmSection compact /><EnterpriseValuePanel compact /></div>

          <div className="mt-4" data-testid="paywall-promo">
            <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-2"><Gift className="w-3.5 h-3.5 text-cobalt" /> Have a promo code?</div>
            <div className="flex gap-2">
              <input value={promo} onChange={(e) => setPromo(e.target.value)} data-testid="paywall-promo-input" placeholder="Enter code"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-cobalt uppercase" />
              <button onClick={applyPromo} disabled={applying || !promo.trim()} data-testid="paywall-promo-apply"
                className="px-4 py-2.5 rounded-xl bg-cobalt-light text-cobalt text-sm font-semibold hover:bg-cobalt hover:text-white transition-all disabled:opacity-50">
                {applying ? "Applying…" : "Apply"}
              </button>
            </div>
          </div>

          <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="membership-terms-checkbox">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 accent-cobalt" />
            <span className="text-sm text-slate-600">I accept the membership terms and authorize the recurring monthly fee until canceled.</span>
          </label>

          <button onClick={pay} disabled={busy || !accepted} data-testid="membership-pay-btn"
            className="mt-5 w-full flex items-center justify-center gap-2 bg-navy text-white py-4 rounded-full font-semibold hover:-translate-y-0.5 transition-all active:scale-[0.99] disabled:opacity-60 disabled:translate-y-0">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
            {busy ? "Redirecting to secure checkout…" : `Activate membership — ${money(monthly)}/mo`}
          </button>
          <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Secured by Stripe</span>
            <a href="/attorney/billing" data-testid="paywall-manage-billing" className="flex items-center gap-1 hover:text-navy"><CreditCard className="w-3.5 h-3.5" /> Manage billing</a>
            <button onClick={logout} data-testid="membership-logout" className="flex items-center gap-1 hover:text-navy"><LogOut className="w-3.5 h-3.5" /> Log out</button>
          </div>
        </div>
      </div>
    </div>
  );
}
